import React, { useEffect, useState } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import { RsaDecryptor, DecryptAttachmentBrowser } from '../../services/e2ee';
import { REACT_APP_E2EE_MEDIA_SIZE_PREVIEW_LIMIT } from '../../config';
import {
  LoadingContainer,
  ErrorContainer,
  DecryptedImage,
  DecryptedVideo,
  LargeMediaCard,
  LargeMediaIconContainer,
  LargeMediaTitle,
  LargeMediaSubtitle,
  LargeMediaButton,
  StyledVideoIcon,
  StyledImageIcon,
} from './styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { FaTimes } from 'react-icons/fa';

// 1. Fast in-memory cache for fast tab-switches (Guid -> { url, mimeType })
const decryptedCache = new Map();

// Track progress of active downloads: Guid -> percentage (0-100)
const activeDownloads = new Map();

// Track callbacks to update progress on mounted components: Guid -> Set of callbacks
const progressListeners = new Map();

// Track the ongoing download/decryption promises: Guid -> Promise
const activePromises = new Map();

const activeLoads = new Map();

// Track active abort controllers for previews: Guid -> AbortController
const activeAbortControllers = new Map();

const cancelPreviewDownload = (guid) => {
  if (activeAbortControllers.has(guid)) {
    activeAbortControllers.get(guid).abort();
    activeAbortControllers.delete(guid);
  }
};

const updateDownloadProgress = (guid, percent) => {
  activeDownloads.set(guid, percent);
  const listeners = progressListeners.get(guid);
  // console.log(
  //   `[E2E Progress] Set Guid: ${guid} to ${percent}%. Listeners count: ${
  //     listeners ? listeners.size : 0
  //   }`
  // );
  if (listeners) {
    listeners.forEach((cb) => cb(percent));
  }
};

// 2. Persistent browser database (IndexedDB)
const DB_NAME = 'squre_e2ee_media_cache';
const STORE_NAME = 'media_blobs';

const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = (e) => resolve(e.target.result);
    request.onerror = (e) => {
      console.error('[E2E Cache] Failed to open IndexedDB:', e.target.error);
      reject(e.target.error);
    };
  });
};

const getCachedBlob = async (guid) => {
  try {
    // console.log(`[E2E Cache] Checking IndexedDB for Guid: ${guid}`);
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(guid);
      request.onsuccess = () => {
        // if (request.result) {
        //   console.log(`[E2E Cache] Cache HIT in IndexedDB for Guid: ${guid}`);
        // } else {
        //   console.log(`[E2E Cache] Cache MISS in IndexedDB for Guid: ${guid}`);
        // }
        resolve(request.result);
      };
      request.onerror = (e) => {
        console.error(
          `[E2E Cache] Read error for Guid: ${guid}`,
          e.target.error
        );
        reject(e.target.error);
      };
    });
  } catch (err) {
    console.error('[E2E Cache] IndexedDB get error:', err);
    return null;
  }
};

const cacheBlob = async (guid, blob) => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(blob, guid);
      request.onsuccess = () => {
        // console.log(
        //   `[E2E Cache] Successfully cached decrypted file in IndexedDB for Guid: ${guid}`
        // );
        resolve();
      };
      request.onerror = (e) => {
        console.error(
          `[E2E Cache] Failed to write Guid: ${guid} to IndexedDB`,
          e.target.error
        );
        reject(e.target.error);
      };
    });
  } catch (err) {
    console.error('[E2E Cache] IndexedDB put error:', err);
  }
};

const clearIndexedDB = async () => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();
      request.onsuccess = () => {
        console.log('[E2E Cache] Cleared all files from IndexedDB.');
        resolve();
      };
      request.onerror = (e) => reject(e.target.error);
    });
  } catch (err) {
    console.error('[E2E Cache] IndexedDB clear error:', err);
  }
};

const getMimeType = (attachmentType) => {
  switch (attachmentType) {
    case 0:
      return 'image/png';
    case 1:
      return 'image/jpeg';
    case 20:
      return 'video/mp4';
    default:
      return 'application/octet-stream';
  }
};

const formatSize = (kb) => {
  if (!kb) return '0 KB';
  const mb = kb / 1024;
  return `${mb.toFixed(2)} MB`;
};

// Decryption queue to prevent concurrent CPU locking
let decryptionQueue = Promise.resolve();
const queueDecryption = (decryptFn) => {
  return new Promise((resolve, reject) => {
    decryptionQueue = decryptionQueue.then(async () => {
      try {
        // Yield to the browser's main thread for 50ms to allow UI rendering
        await new Promise((resolve) => setTimeout(resolve, 50));
        const result = await decryptFn();
        resolve(result);
      } catch (err) {
        reject(err);
      }
    });
  });
};

const EncryptedMedia = ({ att, setAttSeleted, urlDowload }) => {
  // 1. Declare variables first
  const limitMB = parseInt(REACT_APP_E2EE_MEDIA_SIZE_PREVIEW_LIMIT || '10', 10);
  const LIMIT_MB_KB = limitMB * 1024;
  const isLargeFile = att.Size >= LIMIT_MB_KB;
  const [isHoveredCancel, setIsHoveredCancel] = useState(false);

  // 2. Declare state hooks using those variables
  const [mediaUrl, setMediaUrl] = useState(() => {
    if (decryptedCache.has(att.Guid)) {
      return decryptedCache.get(att.Guid).url;
    }
    return null;
  });

  // If it's already in memory or currently downloading, show loading spinner
  const [loading, setLoading] = useState(() => {
    return (
      !decryptedCache.has(att.Guid) &&
      (activeDownloads.has(att.Guid) || !isLargeFile)
    );
  });

  const [error, setError] = useState(false);

  // If it is already downloading, auto-enable preview requested state
  const [userRequestedPreview, setUserRequestedPreview] = useState(() => {
    return activeDownloads.has(att.Guid);
  });

  // Initialize progress from the global active downloads map
  const [downloadProgress, setDownloadProgress] = useState(() => {
    return activeDownloads.get(att.Guid) || 0;
  });

  useEffect(() => {
    let active = true;

    // Listen for progress updates for this file
    // Listen for progress updates for this file
    const handleProgressUpdate = (percent) => {
      // console.log(
      //   `[E2E Listener] UI received progress for ${att.Guid}: ${percent}% (active: ${active})`
      // );
      if (active) {
        setDownloadProgress(percent);
      }
    };

    if (!progressListeners.has(att.Guid)) {
      progressListeners.set(att.Guid, new Set());
    }
    progressListeners.get(att.Guid).add(handleProgressUpdate);

    const loadAndDecryptMedia = async () => {
      // console.log(
      //   `[E2E Load] Checking Guid: ${att.Guid}. activeLoads: ${activeLoads.has(
      //     att.Guid
      //   )}, activeDownloads: ${activeDownloads.get(att.Guid)}%`
      // );

      // If this file is already loading/fetching, wait for the existing promise
      if (activeLoads.has(att.Guid)) {
        await activeLoads.get(att.Guid);
        if (active) {
          const cached = decryptedCache.get(att.Guid);
          if (cached) {
            setMediaUrl(cached.url);
            setLoading(false);
          }
        }
        return;
      }

      // Create the single source-of-truth promise synchronously
      // Create the download promise and store it globally
      const loadPromise = (async () => {
        // A. Check in-memory Cache (Fast Hit)
        if (decryptedCache.has(att.Guid)) {
          return;
        }

        // B. Check persistent IndexedDB Cache
        const cachedBlob = await getCachedBlob(att.Guid);
        if (cachedBlob) {
          const createdUrl = URL.createObjectURL(cachedBlob);
          const mimeType = getMimeType(att.AttachmentType);
          decryptedCache.set(att.Guid, { url: createdUrl, mimeType });
          return;
        }

        // C. If large file and user hasn't clicked "Load Preview" yet, don't download
        if (isLargeFile && !userRequestedPreview) {
          return;
        }

        if (active) {
          setLoading(true);
          setError(false);
        }

        updateDownloadProgress(att.Guid, 1); // Start progress

        // Create AbortController for this preview download
        const abortController = new AbortController();
        activeAbortControllers.set(att.Guid, abortController);

        try {
          const response = await fetch(`${urlDowload}/${att.Guid}`, {
            signal: abortController.signal,
          });
          if (!response.ok) throw new Error('Failed to download attachment');

          const contentLength = response.headers.get('content-length');
          const totalBytes = contentLength
            ? parseInt(contentLength, 10)
            : att.Size
            ? att.Size * 1024
            : 0;

          let done = false;
          const reader = response.body.getReader();
          let receivedBytes = 0;
          const chunks = [];

          while (!done) {
            const result = await reader.read();
            done = result.done;
            if (result.value) {
              chunks.push(result.value);
              receivedBytes += result.value.length;
              if (totalBytes > 0) {
                const percent = Math.round((receivedBytes / totalBytes) * 100);
                updateDownloadProgress(att.Guid, percent);
              }
            }
          }

          const chunksAll = new Uint8Array(receivedBytes);
          let position = 0;
          for (let chunk of chunks) {
            chunksAll.set(chunk, position);
            position += chunk.length;
          }
          const arrayBuffer = chunksAll.buffer;

          const decryptedKey = await RsaDecryptor(att.EncryptionKey);
          const decryptedIv = await RsaDecryptor(att.Iv);

          const decryptedBytes = await queueDecryption(() =>
            DecryptAttachmentBrowser(arrayBuffer, decryptedIv, decryptedKey)
          );

          if (!decryptedBytes || decryptedBytes.length === 0) {
            throw new Error('Decrypted binary data is empty');
          }

          const mimeType = getMimeType(att.AttachmentType);
          const fileBlob = new Blob([decryptedBytes], { type: mimeType });
          const createdUrl = URL.createObjectURL(fileBlob);

          // Store in caches
          decryptedCache.set(att.Guid, { url: createdUrl, mimeType });
          await cacheBlob(att.Guid, fileBlob);
        } finally {
          // Clean up the abort controller once complete/aborted
          activeAbortControllers.delete(att.Guid);
        }
      })();

      // Store the promise synchronously to block any duplicate downloads immediately
      activeLoads.set(att.Guid, loadPromise);

      try {
        await loadPromise;
        activeLoads.delete(att.Guid);
        activeDownloads.delete(att.Guid);

        if (active) {
          const cached = decryptedCache.get(att.Guid);
          if (cached) {
            setMediaUrl(cached.url);
            setLoading(false);
          }
        }
      } catch (err) {
        activeLoads.delete(att.Guid);
        activeDownloads.delete(att.Guid);

        if (active) {
          if (err.name === 'AbortError') {
            // Reset the UI back to the fresh "Load Preview" button
            setError(false);
            setLoading(false);
            setUserRequestedPreview(false);
            setDownloadProgress(0);
          } else {
            console.error('Failed to load decrypted media preview:', err);
            setError(true);
            setLoading(false);
          }
        }
      }
    };

    loadAndDecryptMedia();

    return () => {
      active = false;
      // Remove progress listener on unmount
      if (progressListeners.has(att.Guid)) {
        progressListeners.get(att.Guid).delete(handleProgressUpdate);
      }
    };
  }, [
    att.Guid,
    att.EncryptionKey,
    att.Iv,
    att.AttachmentType,
    att.Size,
    urlDowload,
    userRequestedPreview,
  ]);

  if (!mediaUrl && isLargeFile && !userRequestedPreview) {
    const isVideo = att.AttachmentType === 20;

    return (
      <LargeMediaCard>
        <LargeMediaIconContainer>
          {isVideo ? (
            <StyledVideoIcon size={40} />
          ) : (
            <StyledImageIcon size={40} />
          )}
        </LargeMediaIconContainer>
        <LargeMediaTitle>{att.Name}</LargeMediaTitle>
        <LargeMediaSubtitle>
          Large Media ({formatSize(att.Size)})
        </LargeMediaSubtitle>
        <LargeMediaButton
          onClick={() => {
            setUserRequestedPreview(true);
          }}
        >
          Load Preview
        </LargeMediaButton>
      </LargeMediaCard>
    );
  }

  function CircularProgressWithLabel(props) {
    return (
      <Box position="relative" display="inline-flex">
        <CircularProgress variant="determinate" {...props} />
        <Box
          top={0}
          left={0}
          bottom={0}
          right={0}
          position="absolute"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Typography variant="caption" component="div" color="textSecondary">
            {`${Math.round(props.value)}%`}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (loading) {
    return (
      <LoadingContainer
        style={{
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onClick={() => cancelPreviewDownload(att.Guid)}
        onMouseEnter={() => setIsHoveredCancel(true)}
        onMouseLeave={() => setIsHoveredCancel(false)}
        title="Click to cancel preview load"
      >
        {isHoveredCancel ? (
          // Hover state: Display the "X" Close icon
          <div
            style={{
              width: 45,
              height: 45,
              borderRadius: '50%',
              backgroundColor: 'rgba(255, 64, 129, 0.1)', // Subtle pink background
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ff4081',
              border: '2px solid #ff4081',
            }}
          >
            <FaTimes size={16} />
          </div>
        ) : // Default state: Display the progress spinner
        downloadProgress > 0 ? (
          <CircularProgressWithLabel
            value={downloadProgress}
            color="secondary"
            size={45}
          />
        ) : (
          <CircularProgress color="secondary" size={30} />
        )}
      </LoadingContainer>
    );
  }

  if (error) {
    return <ErrorContainer>Failed to load secure preview</ErrorContainer>;
  }

  if (att.AttachmentType === 0 || att.AttachmentType === 1) {
    return (
      <DecryptedImage
        alt={att.Name}
        src={mediaUrl}
        onClick={() => setAttSeleted({ ...att, mediaUrl })}
      />
    );
  }

  if (att.AttachmentType === 20) {
    return (
      <DecryptedVideo controls>
        <source src={mediaUrl} type="video/mp4" />
        <p title={att.Name}>{att.Name}</p>
      </DecryptedVideo>
    );
  }

  return null;
};

export const clearDecryptedMediaCache = async () => {
  decryptedCache.forEach((item) => {
    URL.revokeObjectURL(item.url);
  });
  decryptedCache.clear();
  await clearIndexedDB();
};

export {
  EncryptedMedia,
  getCachedBlob,
  cacheBlob,
  activeLoads,
  activeDownloads,
};
